/* Company Confidential, Copyright (c) 2024 Signant Health. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Header } from '@signanthealth/modular-ui';
import { Typography, Link as MuiLink } from '@mui/material';
import HospitalIcon from '../../../assets/images/icons/hospital.svg';
import { Link as RouterLink } from 'react-router-dom';
import {
  STUDIES_PAGE,
  USER_MANAGEMENT,
  APP_NAME,
  HEADER_BRAND_NAME,
  ACCOUNT,
  HELPDESK_LABEL,
  CLAIM_YOUR_ACCOUNT
} from '../../../const';
import { home, userManagement } from '../../../constants/localUrls';
import Loading from '../../loading/loading.component';
import { deleteCookie } from '../../../utils/helpers';
import { getUserInfoFromAccessToken, getUserInfoFromIdToken } from '../../../utils/cookies';
import { clearStorage, cookieUtilsRead } from '../../../utils/cookieHelpers';

class ModularHeader extends Component {
  constructor(props) {
    super(props);
    this.updateSelectedSponsor = this.updateSelectedSponsor.bind(this);
    this.signOut = this.signOut.bind(this);
    this.openHelpdesk = this.openHelpdesk.bind(this);
    this.preventPageRefresh = this.preventPageRefresh.bind(this);
    this.showMigrationModal = this.showMigrationModal.bind(this);
  }

  componentDidMount() {
    // Workaround to prevent the brand-link on the left side of the
    // header from doing a full page refresh, but instead use similar
    // navigation as react-router links.
    const brandLink = document.querySelector('.modularheader-container h1 a');
    if (brandLink) {
      const dest = brandLink.getAttribute('href');
      if (dest === home()) {
        brandLink.addEventListener('click', this.preventPageRefresh);
      }
    }
  }

  componentWillUnmount() {
    const brandLink = document.querySelector('.modularheader-container h1 a');
    if (brandLink) {
      const dest = brandLink.getAttribute('href');
      if (dest === home()) {
        brandLink.removeEventListener('click', this.preventPageRefresh);
      }
    }
    clearStorage();
  }

  preventPageRefresh(event) {
    event.stopPropagation();
    event.preventDefault();
    if (this.props.location.pathname !== home()) {
      this.props.history.push(home());
    }
  }

  signOut() {
    let logoutUrl = this.props.logoutUrl;
    this.props.signOut(logoutUrl);
  }

  updateSelectedSponsor() {
    this.props.changeSponsor(this.props.selectedSponsor);
  }

  openHelpdesk() {
    this.props.openHelpdesk();
  }

  getNavItems(error, sponsorname, fetchingUserDetails, isFetching) {
    const items = [];
    if (fetchingUserDetails || isFetching) {
      items.push(
        <Typography key="menuitem_loading" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Loading />
        </Typography>
      );
    } else if (sponsorname) {
      items.push(
        <Typography
          key="menuitem_sponsor"
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          onClick={this.updateSelectedSponsor}
        >
          {/* Icon SVG taken directly from UX mockup, same icon not found in fontawesome v6 */}
          <img style={{ width: '16px', height: '16px', marginRight: '4px' }} src={HospitalIcon} />
          {sponsorname}
        </Typography>
      );
    }
    if (!error) {
      items.push(
        <RouterLink key="menuitem_studies" to={home()}>
          {STUDIES_PAGE}
        </RouterLink>
      );
      items.push(
        <RouterLink key="menuitem_usermanagement" to={userManagement()}>
          {USER_MANAGEMENT}
        </RouterLink>
      );
    }
    items.push(
      <Typography
        key="menuitem_help"
        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        onClick={this.openHelpdesk}
      >
        {HELPDESK_LABEL}
      </Typography>
    );
    return items;
  }

  showMigrationModal() {
    this.props.showMigrationModal();
    deleteCookie('tc-idportal-migration');
  }

  render() {
    const {
      error,
      fetchingUserDetails,
      isFetching,
      idPortalApiUrl,
      idPortalUrl,
      clientApp,
      accessToken,
      idPortalMigration
    } = this.props;

    const idToken = cookieUtilsRead('id_token');
    const { initials } = Boolean(idToken) ? getUserInfoFromIdToken(idToken) : getUserInfoFromAccessToken(accessToken);
    const shouldRenderAppSwitcher = idPortalUrl && idPortalApiUrl && clientApp;
    const appSwitcherProps = shouldRenderAppSwitcher && { idPortalUrl, idPortalApiUrl, accessToken, clientApp };
    let profileMenuItems = [];
    shouldRenderAppSwitcher &&
      profileMenuItems.push(
        <MuiLink
          key="account"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
          href={`${idPortalUrl}/account`}
        >
          {ACCOUNT}
        </MuiLink>
      );

    const isEligibleForMigration =
      idPortalMigration &&
      (idPortalMigration.nextStep?.action === 'SHOW_POPUP' ||
        idPortalMigration?.nextStep?.action === 'SHOW_POPUP_FORCED');
    isEligibleForMigration &&
      profileMenuItems.push(
        <MuiLink
          key="claim-your-account"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={this.showMigrationModal}
        >
          {CLAIM_YOUR_ACCOUNT}
        </MuiLink>
      );

    let sponsorname;
    if (this.props.selectedSponsor) {
      sponsorname = this.props.selectedSponsor.sponsorname;
    }
    return (
      <Header
        trademarkSymbol="&reg;"
        appName={APP_NAME}
        appSwitcherProps={appSwitcherProps}
        brandName={HEADER_BRAND_NAME}
        homeUrl={home()}
        logout={this.signOut}
        navigationItems={this.getNavItems(error, sponsorname, fetchingUserDetails, isFetching)}
        userInitials={initials}
        profileMenuItems={profileMenuItems}
      />
    );
  }
}

ModularHeader.propTypes = {
  error: PropTypes.object,
  selectedSponsor: PropTypes.object,
  changeSponsor: PropTypes.func,
  openHelpdesk: PropTypes.func,
  signOut: PropTypes.func,
  logoutUrl: PropTypes.string,
  location: PropTypes.object,
  fetchingUserDetails: PropTypes.bool,
  isFetching: PropTypes.bool,
  user: PropTypes.object,
  idPortalUrl: PropTypes.string,
  idPortalApiUrl: PropTypes.string,
  clientApp: PropTypes.string,
  history: PropTypes.object,
  idPortalMigration: PropTypes.object,
  accessToken: PropTypes.string
};

export default ModularHeader;
